<script lang="ts">
  // BJL-CHECK: Timeline, Card, Alert, Button — considered, none used.
  // Timeline is the closest fit and still wrong twice over: every row here is a BUTTON that
  // sets the alight stop (Timeline is presentational), and the rail, dot fills and plate are
  // a specific physical screen's livery rather than a theme. Card/Alert would each frame this
  // in app chrome, which is the one thing this panel must not look like. See
  // artifacts/deviations/RiderPanel.md.
  //
  // The boarded-BUS stop list. A bus rider is not looking at a rollsign: they are looking at
  // the backlit LCD over the farebox, which lists the run TOP TO BOTTOM — green "NEXT STOP"
  // kicker, the stop itself in white on the black, and everything after it threaded on one
  // rail of hollow dots. That screen is what this
  // component is. The subway's face (RidingPanel's own strip) runs left to right
  // on the diagonal because THAT is how the strip map above a subway door is drawn; copying
  // it onto a bus was showing the rider a device their vehicle doesn't have.
  //
  // The upcoming list is on the SAME black as the rest of the panel — it used to sit on a
  // pale blue plate, and the panel now reads as one surface. What separates the next stop
  // from the rest is the type (white, 20px vs. tinted, 14px) and its filled dot, so nothing
  // was lost with the plate except a seam. Those two are now the WHOLE separation: the
  // "UPCOMING STOPS" kicker that used to label the list is gone too, so if the next stop ever
  // stops being the biggest, whitest thing on this screen the list loses its head.
  //
  // Sizes are raw px, not the `Type` scale, for the same reason the rollsign's are: this is a
  // depiction of a screen with its own type sizes, not a screen of the app's own text.
  import {
    LCD_DIM,
    LCD_DOT,
    LCD_FONT,
    LCD_GREEN,
    LCD_INK,
    LCD_LIST_INK,
    LCD_LIST_LABEL,
  } from '../../ui/theme'
  import { useStopsStore } from '../../store/stops'
  import { storeSelect } from '../../lib/store.svelte'
  import type { Snippet } from 'svelte'
  import type { Stop } from '../../store/rider'
  import { fmtCountdown, fmtShort } from './shell'

  interface Props {
    /** Stops still ahead, nearest first. `stops[0]` is the next stop. */
    stops: Stop[]
    destinationStopId: string | null
    /** Tapping a stop flags it as where the rider gets off (and tapping it again clears it). */
    onPick: (stopId: string) => void
    /**
     * Rendered bottom-LEFT — the crowding control. A snippet rather than a `vehicle` prop so
     * this component still knows nothing about a VehiclePosition: it owns the footer's
     * geometry, RidingPanel owns what goes in it. Same reason `footerRight` is a snippet.
     */
    footerLeft?: Snippet
    /**
     * Rendered bottom-RIGHT — the panel's control cluster, which is the 3D toggle (the ✕ that
     * used to close it rides the route line at the top of the face instead). It sits in this
     * footer rather than in the headline row above so the bus face's app chrome is all on one
     * line and the headline keeps as much of the row as it can; see RidingPanel's comment at
     * the call site.
     */
    footerRight?: Snippet
    isArriving?: boolean
  }

  const {
    stops,
    destinationStopId,
    onPick,
    footerLeft,
    footerRight,
    isArriving: propIsArriving,
  }: Props = $props()

  const isArriving = $derived.by(() => {
    if (propIsArriving !== undefined) return propIsArriving
    if (stops[0]?.arrivalTime) {
      const arrTime = stops[0].arrivalTime > 1e11 ? stops[0].arrivalTime : stops[0].arrivalTime * 1000
      const diff = arrTime - Date.now()
      if (diff <= 60000 && diff >= -30000) return true
    }
    return false
  })

  const stopsData = storeSelect(useStopsStore, (s) => s.stops)
  const busStopsData = storeSelect(useStopsStore, (s) => s.busStops)

  const accessibleByStop = $derived.by(() => {
    const map = new Map<string, boolean>()
    for (const s of [...stopsData.value, ...busStopsData.value]) {
      if (s.accessible) {
        map.set(s.id, true)
        for (const m of s.memberIds ?? []) {
          map.set(m, true)
        }
      }
    }
    return map
  })

  function isAccessible(stop: Stop): boolean {
    if (stop.accessible !== undefined) return stop.accessible
    return accessibleByStop.get(stop.stopId) ?? false
  }

  const next = $derived(stops[0] ?? null)
  const rest = $derived(stops.slice(1))

  // The rail's own geometry. The dots — filled for the next stop, hollow for the rest — are
  // centred ON the rail, so every x below is derived from these two rather than eyeballed:
  // one gutter is what makes the next stop's dot and the plate's dots line up down one line,
  // across the gap between the black and the plate.
  const RAIL_X = 9
  const RAIL_W = 3
  const RAIL_MID = RAIL_X + RAIL_W / 2
  const NEXT_DOT = 15
  const STOP_DOT = 13
  /** Where every label starts: clear of the widest dot, with room to breathe. */
  const GUTTER = 30

  function isDest(stop: Stop): boolean {
    return stop.stopId === destinationStopId
  }

  function pickLabel(name: string, dest: boolean): string {
    return dest ? `Don't get off at ${name}` : `Get off at ${name}`
  }
</script>

<!-- NO bottom gutter: the footer below is this face's control row, and `PANEL_BOTTOM_GUTTER`
     on the drawer is all that separates it from the rim — exactly what the train's control row
     gets, so the two faces end the same distance above the tab bar. There used to be 22px here
     as well, from back when this panel was anchored to the page region's `bottom: 0` and the
     tab bar started immediately below it; that bar has already paid the navigation inset (see
     `panelShell`), so the 22 was ~22px of black under the controls. It was also never the
     rollsign figure it claimed to match — the strip's scroll hint is an absolutely-positioned
     arrow at `bottom: 22px`, not a gutter. -->
<div
  style="
    flex: 1; min-height: 0; display: flex; flex-direction: column;
    padding: 10px 18px 0; font-family: {LCD_FONT}; position: relative; z-index: 1;
  "
>
  {#if !next}
    <span style="color: {LCD_DIM}; font-size: 15px; font-weight: 600; letter-spacing: 0.08em;">
      LOADING STOP DATA…
    </span>
  {:else}
    <!-- NEXT STOP: the kicker, then the stop. The only green and the only white on the
         screen, because between them they are the whole message. -->
    <span
      style="
        flex-shrink: 0; color: {LCD_GREEN}; font-size: 26px; font-weight: 700;
        letter-spacing: 0.02em; line-height: 1.1;
      "
    >{isArriving ? 'THIS IS' : 'NEXT STOP'}</span>

    <button
      onclick={() => onPick(next.stopId)}
      aria-label={pickLabel(next.stopName, isDest(next))}
      aria-pressed={isDest(next)}
      style="
        flex-shrink: 0; position: relative; display: block; width: 100%;
        margin-top: 6px; padding: 4px 0 4px {GUTTER}px;
        background: none; border: none; cursor: pointer;
        font-family: inherit; text-align: left;
      "
    >
      <div
        style="
          position: absolute; left: {RAIL_MID - NEXT_DOT / 2}px; top: 8px;
          width: {NEXT_DOT}px; height: {NEXT_DOT}px; border-radius: 50%;
          background: {LCD_DOT};
        "
      ></div>
      <!-- Name left, ETA then clock time on the right, on ONE line and off the name's own
           baseline — the same shape every row of the list below already has, so the next stop
           now differs from them only in size and ink rather than in where its time is. It used
           to sit UNDER the name, which put a second green line between the stop and the list
           and read as a row of its own.
           **ETA leads, the timestamp trails**: "3 MIN" is what a rider on board actually reads
           off this line and the wall-clock time is the reference behind it, so the minutes sit
           against the name where the eye lands after it rather than behind eight characters of
           clock. The list below still shows the clock alone — it has no countdown to lead with.
           The name still WRAPS rather than ellipsising (the next stop's full name is the whole
           message on this screen), so `align-items: baseline` is what keeps the time on the
           first line of a two-line name instead of drifting down with the block's centre. The
           pair is `flex: none` + `nowrap`: it is ~106px of the ~294 this button has on a 360px
           phone, and breaking the two figures across a wrap would be worse than a long name
           taking a second line. -->
      <div style="display: flex; align-items: baseline; gap: 10px;">
        <div style="flex: 1; min-width: 0; color: {LCD_INK}; font-size: 20px; font-weight: 700; letter-spacing: 0.01em; line-height: 1.15; text-transform: uppercase;">
          <span>{next.stopName}</span>{#if isAccessible(next)}<span style="font-size: 0.625em; line-height: 1; vertical-align: middle; margin: 0 1px 0 2px;">♿</span>{/if}{#if isDest(next)} 🏁{/if}
        </div>
        <div
          style="
            flex: none; color: {LCD_GREEN}; font-size: 13px; font-weight: 600;
            white-space: nowrap;
          "
        >{fmtCountdown(next.arrivalTime) ? `${fmtCountdown(next.arrivalTime)} · ` : ''}{fmtShort(next.arrivalTime)}</div>
      </div>
    </button>

    <!-- The stops after the next one. No "UPCOMING STOPS" label: the rail threads straight
         down out of the next stop's filled dot, so the list reads as the rest of the same run
         without being told, and the label's 12px + gap was height the scroller wanted. The
         margin below is what the kicker used to sit in. -->
    <div
      style="
        flex: 1; min-height: 0; display: flex; flex-direction: column;
        margin-top: 8px;
      "
    >
      <div style="flex: 1; min-height: 0; overflow-y: auto;">
        {#if rest.length === 0}
          <div style="padding: 0 12px 4px {GUTTER}px; color: {LCD_LIST_LABEL}; font-size: 13px; font-weight: 600;">
            LAST STOP ON THIS RUN
          </div>
        {/if}

        {#each rest as stop, i (stop.stopId)}
          <button
            onclick={() => onPick(stop.stopId)}
            aria-label={pickLabel(stop.stopName, isDest(stop))}
            aria-pressed={isDest(stop)}
            style="
              position: relative; display: flex; align-items: center; gap: 8px; width: 100%;
              padding: 6px 0 6px {GUTTER}px;
              background: none; border: none; cursor: pointer;
              font-family: inherit; text-align: left;
            "
          >
            <!-- Rail line passing through each stop dot -->
            <div
              style="
                position: absolute; left: {RAIL_X}px; top: 0; width: {RAIL_W}px;
                background: {LCD_DOT};
                {i === rest.length - 1 ? 'height: 50%;' : 'bottom: 0;'}
              "
            ></div>
            <div
              style="
                position: absolute; left: {RAIL_MID - STOP_DOT / 2}px; top: 50%;
                margin-top: {-STOP_DOT / 2}px;
                width: {STOP_DOT}px; height: {STOP_DOT}px; border-radius: 50%;
                border: 3px solid {LCD_DOT}; box-sizing: border-box;
                background: {isDest(stop) ? LCD_LIST_INK : 'transparent'};
              "
            ></div>
            <span
              style="
                flex: 1; min-width: 0; color: {LCD_LIST_INK};
                font-size: 14px; font-weight: 700; letter-spacing: 0.01em;
                text-transform: uppercase; overflow: hidden; text-overflow: ellipsis;
                white-space: nowrap;
              "
            ><span>{stop.stopName}</span>{#if isAccessible(stop)}<span style="font-size: 0.625em; line-height: 1; vertical-align: middle; margin: 0 1px 0 2px;">♿</span>{/if}{#if isDest(stop)} 🏁{/if}</span>
            <span style="flex: none; color: {LCD_LIST_LABEL}; font-size: 12px; font-weight: 600;">
              {fmtShort(stop.arrivalTime)}
            </span>
          </button>
        {/each}
      </div>
    </div>

  {/if}

  <!-- The footer, and the only row on this face that is the APP talking rather than the bus:
       crowding on the left, the panel's own controls (3D toggle, ✕) on the right. Everything
       in it is dimmer and smaller than the screen above, so no part of it reads as another row
       of the run.
       Crowding lives HERE rather than up in the headline row, where it went first: it is a
       fixed ~138px wide and will not shrink, so beside the destination it left the headline
       about ten characters and "TO JAMAICA CENTER" rendered as "TO…". This row had the space
       going spare, and the headline is back at full size because nothing competes with it —
       which is also why the control cluster came DOWN here from that row. There is no "TAP A
       STOP" hint any more: every stop on this screen is a button and the footer's width is
       worth more to the two controls than to a caption for behaviour a tap discovers.
       `margin-top: auto` pins the row to the bottom of the panel, so on a three-stop run the
       controls are still on the bottom edge rather than floating under the last stop. With a
       long run the scroller takes the height and the auto margin collapses to nothing.
       Outside the loading branch above, so a rider can still report a packed bus — or leave
       it — while the stop feed is loading. -->
  <div
    style="
      flex-shrink: 0; margin-top: auto; padding: 4px 0 0;
      display: flex; align-items: center; gap: 12px;
    "
  >
    {#if footerLeft}
      <div style="flex: 1; min-width: 0; display: flex; align-items: center; overflow: hidden;">
        {@render footerLeft()}
      </div>
    {/if}
    {#if footerRight}
      <div style="flex: none; margin-left: auto;">{@render footerRight()}</div>
    {/if}
  </div>
</div>
